import React, {useMemo, useState} from 'react';
import {Alert, Button, DatePicker, FlexColumn, FlexRow, Label, Text, TextField} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import PortalApproverPicker from '../../../shared/components/pickers/PortalApproverPicker';
import {usePortalEligibleApprovers} from '../../../shared/hooks/usePortalEligibleApprovers';
import {createToast} from '../../../shared/components/toasts/toast';
import {AssignApproverMutation, commitAsync} from './clientAssetMutations';

export type ApproverRowAsset = {
	id: string;
	assetType: string;
	status: string;
	archived: boolean;
	approverPortalUserId?: string | null;
	approverEmail?: string | null;
};

type Props = {
	asset: ApproverRowAsset;
	/** Relay global id of the project. */
	projectId: string;
	/** The drawer's in-flight flag: every drawer action is disabled while any runs. */
	busy: boolean;
	/** The drawer's runner: one action at a time; a rejection renders inline in the drawer. */
	run: (action: () => Promise<void>) => Promise<void>;
	onChanged: () => void;
};

/**
 * FOR-38. The deliverable's approver, with inline Assign / Reassign while it is awaiting approval.
 * A decided or archived deliverable offers nothing: the workspace refuses a reassign after a decision
 * (NOT_AWAITING_APPROVAL); uploading a new version re-opens approval. The current approver is dropped
 * from the options, as in ReassignSignoffApproverModal, so a PM cannot "reassign" to the same person.
 */
export const ClientAssetApproverRow = ({asset, projectId, busy, run, onChanged}: Props): JSX.Element | null => {
	const {formatMessage} = useIntl();
	const [open, setOpen] = useState(false);
	const [approver, setApprover] = useState<string | null>(null);
	const [deadline, setDeadline] = useState<string | undefined>(undefined);
	const [reason, setReason] = useState('');
	// Fetched only while the form is open: the drawer opens for every asset and most opens never reassign.
	const {approvers, loading, error} = usePortalEligibleApprovers(open ? projectId : null);
	const offered = useMemo(
		() => approvers.filter(a => a.portalUserId !== asset.approverPortalUserId),
		[approvers, asset.approverPortalUserId]
	);

	if (asset.assetType !== 'deliverable') return null;
	const editable = !asset.archived && asset.status === 'awaiting_approval';
	const actionLabel = formatMessage({
		id: asset.approverPortalUserId ? 'client_assets.drawer.reassign_approver' : 'client_assets.drawer.assign_approver',
	});
	const close = () => {
		setOpen(false);
		setApprover(null);
		setDeadline(undefined);
		setReason('');
	};

	const save = () => {
		if (!approver) return;
		const trimmed = reason.trim();
		return run(async () => {
			await commitAsync(AssignApproverMutation, {
				projectId,
				assetId: asset.id,
				approverPortalUserId: approver,
				// Omitted, not null: the workspace reads a missing dueDate as "leave it" and null as "clear it".
				...(deadline ? {dueDate: deadline} : {}),
				reason: trimmed.length > 0 ? trimmed : null,
			});
			createToast({
				duration: 5000,
				variant: 'banner',
				message: (
					<Alert type={Alert.TYPE.SUCCESS} text={formatMessage({id: 'client_assets.drawer.approver_saved_toast'})} />
				),
			});
			// Only reached on success: a refusal rejects above, so the form and its input survive.
			close();
			onChanged();
		});
	};

	return (
		// A plain div rather than the DS FlexColumn, which drops data-cy.
		<div data-cy="client-asset-approver-row">
			<FlexColumn gap="xs">
				<Label>{formatMessage({id: 'client_assets.drawer.approver'})}</Label>
				<FlexRow gap="s" alignItems="center">
					<div data-testid="client-asset-approver-current" data-cy="client-asset-approver-current">
						<Text>{asset.approverEmail ?? formatMessage({id: 'client_assets.drawer.no_approver'})}</Text>
					</div>
					{editable && !open && (
						<Button
							emphasis="low"
							onPress={() => setOpen(true)}
							disabled={busy}
							data-cy="client-asset-approver-open"
						>
							{actionLabel}
						</Button>
					)}
				</FlexRow>
				{editable && open && (
					<FlexColumn gap="s">
						<PortalApproverPicker
							value={approver}
							approvers={offered}
							loading={loading}
							error={error}
							onChange={setApprover}
							placeholder={formatMessage({id: 'client_assets.field.approver_placeholder'})}
							ariaLabel={formatMessage({id: 'client_assets.drawer.approver'})}
							dataCy="client-asset-approver-picker"
						/>
						<Label>{formatMessage({id: 'client_assets.drawer.reassign_deadline'})}</Label>
						<DatePicker
							value={deadline}
							onChange={(dateString: string) => setDeadline(dateString || undefined)}
							aria-label={formatMessage({id: 'client_assets.drawer.reassign_deadline'})}
							data-cy="client-asset-approver-deadline"
						/>
						<Label>{formatMessage({id: 'client_assets.drawer.reassign_reason'})}</Label>
						<TextField
							value={reason}
							onValueChange={(value: string) => setReason(value)}
							aria-label={formatMessage({id: 'client_assets.drawer.reassign_reason'})}
							maxLength={500}
							data-cy="client-asset-approver-reason"
						/>
						<FlexRow gap="s">
							<Button
								emphasis="high"
								onPress={save}
								disabled={!approver || busy}
								data-cy="client-asset-approver-save"
							>
								{actionLabel}
							</Button>
							<Button emphasis="low" onPress={close} disabled={busy} data-cy="client-asset-approver-cancel">
								{formatMessage({id: 'common.cancel'})}
							</Button>
						</FlexRow>
					</FlexColumn>
				)}
			</FlexColumn>
		</div>
	);
};
